import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import {
  Container,
  Accordion,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";
import MatchesInTable from "./MatchesInTable"; // Import the new child component

const API_URL = process.env.REACT_APP_API_URL;

const IndividualMatchManagement = () => {
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [notification, setNotification] = useState({ type: "", msg: "" });
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState("all");

  // Edit Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [confirmCalculatePoints, setConfirmCalculatePoints] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Create Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMatch, setNewMatch] = useState({
    tableName: "",
    math_type: 1,
    player_1: null,
    player_2: null,
    player_3: null,
    player_4: null,
    play_date: new Date().toISOString().slice(0, 10),
    tournament_id: "",
  });

  useEffect(() => {
    const fetchAllDataForYear = async (year) => {
      setLoading(true);
      try {
        const [matchesRes, playersRes, tournamentsRes] = await Promise.all([
          axios.get(`${API_URL}/details/individual-matches?year=${year}`),
          axios.get(`${API_URL}/players?limit=all`),
          axios.get(`${API_URL}/tournaments?year=${year}`),
        ]);
        setGroupedMatches(matchesRes.data);
        setPlayers(playersRes.data);
        setTournaments(tournamentsRes.data);
      } catch (err) {
        setNotification({ type: "danger", msg: `Không thể tải dữ liệu: ${err.message}` });
      } finally {
        setLoading(false);
      }
    };

    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const yearsRes = await axios.get(`${API_URL}/details/individual-matches/years`);
        const availableYears = yearsRes.data;
        setYears(availableYears);

        const yearToFetch = availableYears.length > 0 ? availableYears[0] : new Date().getFullYear();
        setSelectedYear(yearToFetch);
        // Data for the year will be fetched in the next effect run when selectedYear changes.
      } catch (err) {
        setNotification({ type: "danger", msg: `Không thể tải dữ liệu năm: ${err.message}` });
        setLoading(false);
      }
    };

    if (selectedYear === null) {
      fetchInitialData();
    } else {
      fetchAllDataForYear(selectedYear);
    }
  }, [needsRefresh, selectedYear]);

  const playerOptions = useMemo(
    () =>
      players.map((p) => ({
        value: p.id,
        label: `${p.name} (ID: ${p.id})`,
      })),
    [players],
  );

  const handleShowEdit = (match) => {
    // This function is now passed to the child component
    const sets = [
      match.home_sec_1,
      match.guest_sec_1,
      match.home_sec_2,
      match.guest_sec_2,
      match.home_sec_3,
      match.guest_sec_3,
      match.home_sec_4,
      match.guest_sec_4,
      match.home_sec_5,
      match.guest_sec_5,
      match.home_result,
      match.guest_result,
    ].map((s) => (s === null ? "" : s));
    setCurrentMatch({ ...match, sets });
    setShowEditModal(true);
  };
  const handleCloseEdit = () => {
    setShowEditModal(false);
    setConfirmCalculatePoints(false);
  };

  const handleSetsChange = (index, value) => {
    const newSets = [...currentMatch.sets];
    newSets[index] = value;
    setCurrentMatch((prev) => ({ ...prev, sets: newSets }));
  };

  const calculateSetResults = (sets) => {
    let homeWins = 0,
      guestWins = 0;
    for (let i = 0; i < 10; i += 2) {
      if (parseInt(sets[i] || 0) > parseInt(sets[i + 1] || 0)) homeWins++;
      else if (parseInt(sets[i + 1] || 0) > parseInt(sets[i] || 0)) guestWins++;
    }
    sets[10] = homeWins;
    sets[11] = guestWins;
    return sets;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Kiểm tra checkbox xác nhận
    if (!confirmCalculatePoints) {
      setNotification({ type: "warning", msg: "Vui lòng xác nhận tính điểm trước khi lưu." });
      return;
    }

    setIsSaving(true);
    const finalSets = calculateSetResults([...currentMatch.sets]); // Recalculate final results
    try {
      await axios.put(`${API_URL}/details/match-details/${currentMatch.id}`, {
        ...currentMatch,
        sets: finalSets, // Send the corrected sets array
      });

      // Cập nhật local state thay vì reload trang
      setGroupedMatches(prevMatches => {
        return prevMatches.map(tournament => ({
          ...tournament,
          rounds: tournament.rounds.map(round => ({
            ...round,
            matches: round.matches.map(match => {
              if (match.id === currentMatch.id) {
                return {
                  ...match,
                  home_result: finalSets[10],
                  guest_result: finalSets[11],
                  home_sec_1: finalSets[0],
                  guest_sec_1: finalSets[1],
                  home_sec_2: finalSets[2],
                  guest_sec_2: finalSets[3],
                  home_sec_3: finalSets[4],
                  guest_sec_3: finalSets[5],
                  home_sec_4: finalSets[6],
                  guest_sec_4: finalSets[7],
                  home_sec_5: finalSets[8],
                  guest_sec_5: finalSets[9],
                  status: 1, // Đánh dấu đã tính điểm
                };
              }
              return match;
            })
          }))
        }));
      });

      setNotification({ type: "success", msg: "Đã cập nhật kết quả và tính điểm thành công!" });
      handleCloseEdit();
    } catch (err) {
      setNotification({ type: "danger", msg: "Lỗi khi lưu kết quả: " + (err.response?.data || err.message) });
    } finally {
      setIsSaving(false);
    }
  };

  const handleShowCreate = () => setShowCreateModal(true);
  const handleCloseCreate = () => setShowCreateModal(false);

  const handleNewMatchChange = (field, value) => {
    setNewMatch((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      tableName: newMatch.tableName,
      math_type: newMatch.math_type,
      player_1: newMatch.player_1?.value,
      player_2: newMatch.player_2?.value,
      player_3: newMatch.player_3?.value,
      player_4: newMatch.player_4?.value,
      play_date: newMatch.play_date,
      tournament_id: newMatch.tournament_id,
    };
    if (!payload.tableName || !payload.player_1 || !payload.player_3) {
      setNotification({
        type: "warning",
        msg: "Vui lòng điền đầy đủ thông tin: Vòng đấu và VĐV.",
      });
      return;
    }
    try {
      await axios.post(`${API_URL}/details/individual-matches`, payload);
      setNotification({
        type: "success",
        msg: "Tạo trận thủ công thành công.",
      });
      setSelectedYear(new Date(newMatch.play_date).getFullYear());
      setNeedsRefresh((p) => !p); // This will refetch the table names
      handleCloseCreate();
    } catch (err) {
      setNotification({
        type: "danger",
        msg: err.response?.data || "Lỗi khi tạo trận.",
      });
    }
  };

  if (loading) return <Spinner />;

  return (
    <Container>
      <Row className="align-items-center mb-3">
        <Col>
          <h2>Quản lý Trận đấu Cá nhân</h2>
        </Col>
        <Col md="auto">
          <Form.Select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
        </Col>
        <Col md="auto">
          <Form.Select value={selectedTournament} onChange={e => setSelectedTournament(e.target.value)}>
            <option value="all">Tất cả giải đấu</option>
            {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="unidentified">Trận đấu lẻ</option>
          </Form.Select>
        </Col>
        <Col md="auto">
          <Button onClick={handleShowCreate} variant="primary">
            Tạo Trận Thủ Công
          </Button>
        </Col>
      </Row>

      {notification.msg && (
        <Alert
          variant={notification.type}
          dismissible
          onClose={() => setNotification({ type: "", msg: "" })}
        >
          {notification.msg}
        </Alert>
      )}

      <Accordion alwaysOpen>
        {groupedMatches
          .filter(g => selectedTournament === 'all' || String(g.tournament_id) === selectedTournament)
          .map((tournament) => (
            <Accordion.Item key={tournament.tournament_id} eventKey={String(tournament.tournament_id)}>
              <Accordion.Header>{tournament.tournament_name}</Accordion.Header>
              <Accordion.Body>
                <Accordion alwaysOpen>
                  {tournament.rounds.map((round) => (
                    <Accordion.Item key={round.round_name} eventKey={round.round_name}>
                      <Accordion.Header>{round.round_name}</Accordion.Header>
                      <Accordion.Body>
                        <MatchesInTable
                          matches={round.matches}
                          players={players} // Pass players down
                          onEditClick={handleShowEdit}
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Accordion.Body>
            </Accordion.Item>
          ))}
      </Accordion>

      {/* Edit Modal with full content */}
      {currentMatch && (
        <Modal show={showEditModal} onHide={handleCloseEdit} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Cập nhật kết quả trận đấu</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleEditSubmit}>
              <h5>
                {currentMatch.p1_name}{" "}
                {currentMatch.p2_name && `/ ${currentMatch.p2_name}`} vs{" "}
                {currentMatch.p3_name}{" "}
                {currentMatch.p4_name && `/ ${currentMatch.p4_name}`}
              </h5>
              <Table bordered>
                <thead>
                  <tr>
                    {[...Array(5)].map((_, i) => (
                      <th key={i} colSpan="2" className="text-center">
                        Set {i + 1}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {[...Array(10)].map((_, i) => (
                      <td key={i}>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={currentMatch.sets[i]}
                          onChange={(e) => handleSetsChange(i, e.target.value)}
                          disabled={isSaving}
                        />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
              <div className="text-center mt-3">
                <h4>
                  Kết quả: {currentMatch.sets[10]} - {currentMatch.sets[11]}
                </h4>
              </div>

              {/* Checkbox xác nhận tính điểm */}
              <div className="mt-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                <Form.Check
                  type="checkbox"
                  id="confirm-calculate-points"
                  label={
                    <span>
                      <strong>Xác nhận tính điểm:</strong> Tôi đã kiểm tra kết quả và muốn tính điểm cho trận này.
                      <span className="text-danger"> (Điểm sẽ được cộng/trừ vào tài khoản VĐV)</span>
                    </span>
                  }
                  checked={confirmCalculatePoints}
                  onChange={(e) => setConfirmCalculatePoints(e.target.checked)}
                  disabled={isSaving}
                />
              </div>

              <div className="text-end mt-4">
                <Button
                  variant="secondary"
                  onClick={handleCloseEdit}
                  className="me-2"
                  disabled={isSaving}
                >
                  Hủy
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!confirmCalculatePoints || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Đang lưu...
                    </>
                  ) : (
                    "Lưu & Tính điểm"
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      <Modal show={showCreateModal} onHide={handleCloseCreate} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Tạo Trận Thủ Công</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Chọn giải đấu</Form.Label>
                  <Form.Select
                    value={newMatch.tournament_id}
                    onChange={(e) => handleNewMatchChange("tournament_id", e.target.value)}
                  >
                    <option value="">-- Không thuộc giải đấu nào --</option>
                    {tournaments.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tên Vòng đấu</Form.Label>
                  <Form.Select
                    value={newMatch.tableName}
                    onChange={(e) => handleNewMatchChange("tableName", e.target.value)}
                    required
                  >
                    <option value="">-- Chọn vòng đấu --</option>
                    <option value="Vòng bảng">Vòng bảng</option>
                    <option value="1/32">1/32</option>
                    <option value="1/16">1/16</option>
                    <option value="1/8">1/8</option>
                    <option value="Tứ kết">Tứ kết</option>
                    <option value="Bán kết">Bán kết</option>
                    <option value="Chung kết">Chung kết</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ngày thi đấu</Form.Label>
                  <Form.Control
                    type="date"
                    value={newMatch.play_date}
                    onChange={(e) => handleNewMatchChange("play_date", e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Loại trận</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Đơn"
                  name="math_type"
                  value={1}
                  checked={newMatch.math_type === 1}
                  onChange={(e) => handleNewMatchChange("math_type", parseInt(e.target.value))}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Đôi"
                  name="math_type"
                  value={2}
                  checked={newMatch.math_type === 2}
                  onChange={(e) => handleNewMatchChange("math_type", parseInt(e.target.value))}
                />
              </div>
            </Form.Group>

            <Row>
              <Col md={6}>
                <h5>Đội 1</h5>
                <Form.Group className="mb-3">
                  <Form.Label>VĐV 1</Form.Label>
                  <Select
                    options={playerOptions}
                    value={newMatch.player_1}
                    onChange={(option) => handleNewMatchChange("player_1", option)}
                    isClearable
                    placeholder="Chọn VĐV 1"
                  />
                </Form.Group>
                {newMatch.math_type === 2 && (
                  <Form.Group className="mb-3">
                    <Form.Label>VĐV 2</Form.Label>
                    <Select
                      options={playerOptions}
                      value={newMatch.player_2}
                      onChange={(option) => handleNewMatchChange("player_2", option)}
                      isClearable
                      placeholder="Chọn VĐV 2"
                    />
                  </Form.Group>
                )}
              </Col>
              <Col md={6}>
                <h5>Đội 2</h5>
                <Form.Group className="mb-3">
                  <Form.Label>VĐV 3</Form.Label>
                  <Select
                    options={playerOptions}
                    value={newMatch.player_3}
                    onChange={(option) => handleNewMatchChange("player_3", option)}
                    isClearable
                    placeholder="Chọn VĐV 3"
                  />
                </Form.Group>
                {newMatch.math_type === 2 && (
                  <Form.Group className="mb-3">
                    <Form.Label>VĐV 4</Form.Label>
                    <Select
                      options={playerOptions}
                      value={newMatch.player_4}
                      onChange={(option) => handleNewMatchChange("player_4", option)}
                      isClearable
                      placeholder="Chọn VĐV 4"
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>
            <div className="text-end">
              <Button variant="secondary" onClick={handleCloseCreate} className="me-2">Hủy</Button>
              <Button variant="primary" type="submit">Tạo</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default IndividualMatchManagement;
